import React, { useState, useEffect } from 'react';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO, addMonths } from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/format';

const GridCalendar = ({ rooms, bookings, roomTypes, onCellClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState(new Set(['all']));
  const [viewMode, setViewMode] = useState('month'); // 'week', 'month', '2months'
  
  // Calculate date range based on view mode
  const getDateRange = () => {
    let start, end;
    
    switch (viewMode) {
      case 'week':
        // Start from current date, show 7 days forward
        start = currentDate;
        end = addDays(currentDate, 6);
        break;
      case '2months':
        // Start from current date, show 60 days forward
        start = currentDate;
        end = addDays(currentDate, 59);
        break;
      case 'month':
      default:
        // Start from current date, show 30 days forward
        start = currentDate;
        end = addDays(currentDate, 29);
        break;
    }
    
    return eachDayOfInterval({ start, end });
  };
  
  const days = getDateRange();
  
  // Count rooms by type
  const roomTypeCounts = {};
  roomTypes.forEach(type => {
    roomTypeCounts[type.id] = rooms.filter(room => room.room_type_id === type.id).length;
  });
  
  // Filter rooms based on selected types and sort by room number
  const filteredRooms = (selectedRoomTypes.has('all') 
    ? rooms 
    : rooms.filter(room => selectedRoomTypes.has(room.room_type_id)))
    .sort((a, b) => {
      const numA = parseInt(a.room_number) || 0;
      const numB = parseInt(b.room_number) || 0;
      return numA - numB;
    });
  
  // Toggle room type filter
  const toggleRoomType = (typeId) => {
    const newSelected = new Set(selectedRoomTypes);
    
    if (typeId === 'all') {
      setSelectedRoomTypes(new Set(['all']));
    } else {
      newSelected.delete('all');
      
      if (newSelected.has(typeId)) {
        newSelected.delete(typeId);
        if (newSelected.size === 0) {
          newSelected.add('all');
        }
      } else {
        newSelected.add(typeId);
      }
      
      setSelectedRoomTypes(newSelected);
    }
  };
  
  // Check if a room is booked on a specific date (including checkout date for visual)
  const isRoomBooked = (roomId, date) => {
    return bookings.find(booking => {
      // Handle both legacy room_id and new room_ids format
      const bookedRoomIds = booking.room_ids || (booking.room_id ? [booking.room_id] : []);
      
      if (!bookedRoomIds.includes(roomId)) return false;
      
      const checkIn = parseISO(booking.check_in);
      const checkOut = parseISO(booking.check_out);
      
      // Include checkout date for the end visual
      return date >= checkIn && date <= checkOut;
    });
  };
  
  // Check if there's a turnover (checkout and checkin on same date)
  const hasTurnover = (roomId, date) => {
    const bookingsOnDate = bookings.filter(booking => {
      const bookedRoomIds = booking.room_ids || (booking.room_id ? [booking.room_id] : []);
      if (!bookedRoomIds.includes(roomId)) return false;
      
      const checkIn = parseISO(booking.check_in);
      const checkOut = parseISO(booking.check_out);
      const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const checkInDate = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
      const checkOutDate = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
      
      return (checkInDate.getTime() === currentDate.getTime() || checkOutDate.getTime() === currentDate.getTime());
    });
    
    // Check if we have both a checkout and checkin on this date
    const hasCheckout = bookingsOnDate.some(b => {
      const checkOut = parseISO(b.check_out);
      const checkOutDate = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
      const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return checkOutDate.getTime() === currentDate.getTime();
    });
    
    const hasCheckin = bookingsOnDate.some(b => {
      const checkIn = parseISO(b.check_in);
      const checkInDate = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
      const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return checkInDate.getTime() === currentDate.getTime();
    });
    
    return hasCheckout && hasCheckin && bookingsOnDate.length >= 2;
  };
  
  // Show guest name on all cells of the booking
  const shouldShowGuestName = (booking, date) => {
    if (!booking) return false;
    
    const checkIn = parseISO(booking.check_in);
    const checkOut = parseISO(booking.check_out);
    
    // Normalize dates to compare only date parts (ignore time)
    const checkInDate = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
    const checkOutDate = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
    const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Calculate the number of stay days (excluding checkout day)
    const totalDays = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Find the middle day (rounded down) - for display purposes, use the middle of the actual booking cells
    const middleDayOffset = Math.floor((totalDays - 1) / 2);
    const middleDate = new Date(checkInDate);
    middleDate.setDate(middleDate.getDate() + middleDayOffset);
    
    // Show name on the middle day
    return middleDate.getTime() === currentDate.getTime();
  };
  
  // Get booking position in the stay (start/middle/end/single)
  const getBookingPosition = (booking, date) => {
    if (!booking) return null;
    
    const checkIn = parseISO(booking.check_in);
    const checkOut = parseISO(booking.check_out);
    
    // Normalize dates to compare only date parts (ignore time)
    const checkInDate = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
    const checkOutDate = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
    const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const isCheckIn = checkInDate.getTime() === currentDate.getTime();
    const isCheckOut = checkOutDate.getTime() === currentDate.getTime();
    
    // Check if it's a single-day booking (check-in and check-out on same day)
    const isSingleDay = isCheckIn && isCheckOut;
    
    if (isSingleDay) return 'single';
    if (isCheckIn) return 'start';
    if (isCheckOut) return 'end';
    return 'middle';
  };
  
  // Navigate dates based on view mode
  const previousPeriod = () => {
    switch (viewMode) {
      case 'week':
        setCurrentDate(addDays(currentDate, -7));
        break;
      case '2months':
        setCurrentDate(addMonths(currentDate, -2));
        break;
      case 'month':
      default:
        setCurrentDate(addMonths(currentDate, -1));
        break;
    }
  };
  
  const nextPeriod = () => {
    switch (viewMode) {
      case 'week':
        setCurrentDate(addDays(currentDate, 7));
        break;
      case '2months':
        setCurrentDate(addMonths(currentDate, 2));
        break;
      case 'month':
      default:
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  const handleDateSelect = (date) => {
    if (date) {
      setCurrentDate(date);
      setDatePickerOpen(false);
    }
  };
  
  // Get display title based on view mode
  const getDisplayTitle = () => {
    const days = getDateRange();
    const start = days[0];
    const end = days[days.length - 1];
    
    if (viewMode === 'week') {
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } else if (viewMode === '2months') {
      return `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
    } else {
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
  };
  
  // Handle cell click
  const handleCellClick = (room, date, booking) => {
    if (onCellClick) {
      // Use the callback from parent if provided
      onCellClick(room, date, booking);
    } else {
      // Fallback to showing toast
      if (booking) {
        toast.info(`${room.name} - ${booking.guest_name}`, {
          description: `Check-in: ${booking.check_in}\nCheck-out: ${booking.check_out}\nAmount: ${formatCurrency(booking.total_amount)}`
        });
      }
    }
  };
  
  return (
    <div className="h-full flex flex-col">
      {/* Calendar Header with Filters */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <h3 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {getDisplayTitle()}
          </h3>
          
          {/* Date Picker */}
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="calendar-date-picker">
                <CalendarIcon className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={currentDate}
                onSelect={handleDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          
          {/* View Mode Selector */}
          <div className="flex items-center gap-1 ml-2 border rounded-md">
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1 text-xs font-medium transition-colors rounded-l-md ${
                viewMode === 'week'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="view-week"
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                viewMode === 'month'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="view-month"
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('2months')}
              className={`px-3 py-1 text-xs font-medium transition-colors rounded-r-md ${
                viewMode === '2months'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="view-2months"
            >
              2 Months
            </button>
          </div>
          
          {/* Room Type Filters - Inline */}
          <div className="flex items-center gap-2 ml-2">
            <span className="text-xs font-medium text-gray-500">Filter:</span>
            <button
              onClick={() => toggleRoomType('all')}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                selectedRoomTypes.has('all')
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="filter-all-rooms"
            >
              All ({rooms.length})
            </button>
            {roomTypes.map((type) => {
              // Simplified display names
              let displayName = type.name.split(' ')[0]; // Default: first word
              if (type.name.includes('Ground Floor')) displayName = 'Ground';
              else if (type.name.includes('Mountain View')) displayName = 'Mountain';
              else if (type.name.includes('Sea View and Terrace')) displayName = 'Terrace';
              else if (type.name.includes('Sea View')) displayName = 'Sea View';
              else if (type.name.includes('Single')) displayName = 'Single';
              
              return (
                <button
                  key={type.id}
                  onClick={() => toggleRoomType(type.id)}
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                    selectedRoomTypes.has(type.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${type.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {displayName} ({roomTypeCounts[type.id] || 0})
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!selectedRoomTypes.has('all') && (
            <span className="text-xs text-gray-500 mr-2">
              {filteredRooms.length}/{rooms.length} rooms
            </span>
          )}
          <Button variant="outline" size="sm" onClick={goToToday} data-testid="today-btn">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={previousPeriod} data-testid="prev-period-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={nextPeriod} data-testid="next-period-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Calendar Grid - Scrollable */}
      <div className="flex-1 overflow-auto border rounded-lg bg-white shadow-sm">
        <div className="calendar-grid-container h-full">
          <div className="calendar-grid">
          {/* Header Row - Dates */}
          <div className="calendar-header-row">
            <div className="calendar-cell calendar-corner-cell">
              <span className="text-[0.6rem] font-semibold text-gray-600">Rm</span>
            </div>
            {days.map((day, index) => (
              <div 
                key={index} 
                className="calendar-cell calendar-date-header"
              >
                <div className="text-[0.55rem] text-gray-500">{format(day, 'EEE').substring(0, 2)}</div>
                <div className="text-[0.65rem] font-semibold">{format(day, 'd')}</div>
              </div>
            ))}
          </div>
          
          {/* Room Rows */}
          {filteredRooms.map((room) => (
            <div key={room.id} className="calendar-row">
              {/* Room Name Cell */}
              <div className="calendar-cell calendar-room-cell">
                <div className="font-semibold text-[0.7rem] text-gray-700">{room.room_number}</div>
              </div>
              
              {/* Date Cells */}
              {days.map((day, index) => {
                const booking = isRoomBooked(room.id, day);
                const isToday = isSameDay(day, new Date());
                const bookingPosition = booking ? getBookingPosition(booking, day) : null;
                const isTurnover = hasTurnover(room.id, day);
                
                // Determine CSS classes based on booking position
                let cellClasses = 'calendar-cell calendar-day-cell';
                if (isTurnover) {
                  // Special case: both checkout and checkin on same date
                  cellClasses += ' booking-turnover';
                } else if (booking) {
                  // Add payment status class
                  if (booking.payment_status === 'pending_payment' || booking.payment_status === 'pending') {
                    cellClasses += ' booking-pending';
                  }
                  
                  if (bookingPosition === 'single') {
                    cellClasses += ' booking-single';
                  } else if (bookingPosition === 'start') {
                    cellClasses += ' booking-start';
                  } else if (bookingPosition === 'end') {
                    cellClasses += ' booking-end';
                  } else if (bookingPosition === 'middle') {
                    cellClasses += ' booking-middle';
                  }
                } else {
                  cellClasses += ' available-cell';
                }
                
                if (isToday) {
                  cellClasses += ' today-cell';
                }
                
                return (
                  <div
                    key={index}
                    className={cellClasses}
                    onClick={() => handleCellClick(room, day, booking)}
                    title={booking ? `${booking.guest_name} - ${booking.num_guests} guest${booking.num_guests > 1 ? 's' : ''}\nPayment: ${booking.payment_status || 'N/A'}` : ''}
                    data-testid={`cell-${room.room_number}-${format(day, 'yyyy-MM-dd')}`}
                  >
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-xs flex-shrink-0 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500 rounded"></div>
          <span>Paid</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-400 rounded"></div>
          <span>Pending Payment</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 relative rounded overflow-hidden">
            <div className="absolute right-0 top-0 w-1/2 h-full bg-blue-500"></div>
            <div className="absolute left-0 top-0 w-1/2 h-full bg-white border-r border-gray-300"></div>
          </div>
          <span>Check-in</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 relative rounded overflow-hidden">
            <div className="absolute left-0 top-0 w-1/2 h-full bg-blue-500"></div>
            <div className="absolute right-0 top-0 w-1/2 h-full bg-white border-l border-gray-300"></div>
          </div>
          <span>Check-out</span>
        </div>
      </div>
    </div>
  );
};

export default GridCalendar;
